'use client';
import React from 'react';
import { CheckCircle } from 'lucide-react';

interface Props {
  gsv: number;
  g1Count: number;
  ebRate: number;
  hasEEB: boolean;
  hasDblG1: boolean;
}

export default function WealthMaximizerComparison({ gsv, g1Count, ebRate, hasEEB, hasDblG1 }: Props) {
  const execBonus = gsv * ebRate;
  const eebBonus = hasEEB ? gsv * 0.05 : 0;
  const dblG1Bonus = hasDblG1 && g1Count > 0 ? gsv * 0.10 : 0;
  const breakawayBonus = g1Count >= 1 ? gsv * (gsv >= 3000 ? 0.05 : 0.025) : 0;

  const volumePath = execBonus + eebBonus + dblG1Bonus;
  const depthPath = execBonus + breakawayBonus;
  const winner = volumePath >= depthPath ? 'volume' : 'depth';
  const saving = Math.abs(volumePath - depthPath);

  const volumeComponents = [
    { label: `Executive Bonus (${(ebRate * 100).toFixed(0)}%)`, value: execBonus, desc: `${(gsv / 1000).toFixed(1)}K GSV × ${(ebRate * 100).toFixed(0)}%` },
    { label: 'Extra Executive Bonus (EEB +5%)', value: eebBonus, desc: hasEEB ? 'GSV ≥ 3,000' : 'Requires 3,000+ GSV', disabled: !hasEEB },
    { label: `Double G1 Bonus (DBLG1 10%)`, value: dblG1Bonus, desc: hasDblG1 && g1Count > 0 ? `${g1Count} G1 × 10% GSV` : 'Requires 3K GSV + G1 execs', disabled: !hasDblG1 || g1Count === 0 },
  ];

  const depthComponents = [
    { label: `Executive Bonus (${(ebRate * 100).toFixed(0)}%)`, value: execBonus, desc: `${(gsv / 1000).toFixed(1)}K GSV × ${(ebRate * 100).toFixed(0)}%` },
    { label: `Breakaway Exec Bonus (${gsv >= 3000 ? '5' : '2.5'}%)`, value: breakawayBonus, desc: g1Count >= 1 ? `${g1Count} G1 execs × ${gsv >= 3000 ? '5' : '2.5'}%` : 'No G1 breakaway execs', disabled: g1Count === 0 },
  ];

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-zinc-300">Wealth Maximizer — Path Comparison</h3>
        <div className="flex items-center gap-2 text-xs text-emerald-400 font-semibold">
          <CheckCircle size={13} />
          <span>{winner === 'volume' ? 'Volume Maximizer' : 'Depth Maximizer'} selected — saves ${saving.toFixed(2)} vs alternative</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Volume Path */}
        <div className={`rounded-xl border p-4 ${winner === 'volume' ? 'bg-amber-500/8 border-amber-500/30' : 'bg-zinc-800/30 border-zinc-700'}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-bold text-zinc-200">Volume Maximizer</p>
              <p className="text-[10px] text-zinc-600">EB + EEB + DBLG1</p>
            </div>
            {winner === 'volume' && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
                <CheckCircle size={9} /> Selected
              </span>
            )}
          </div>
          <div className="space-y-2">
            {volumeComponents.map(c => (
              <div key={`vc-${c.label}`} className={`flex items-center justify-between ${c.disabled ? 'opacity-40' : ''}`}>
                <div>
                  <p className="text-[10px] font-semibold text-zinc-300">{c.label}</p>
                  <p className="text-[9px] text-zinc-600">{c.desc}</p>
                </div>
                <span className={`text-sm font-bold font-mono ${c.value > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>${c.value.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-700 mt-3 pt-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-400">Volume Path Total</span>
            <span className={`text-xl font-bold font-mono ${winner === 'volume' ? 'text-amber-400' : 'text-zinc-400'}`}>${volumePath.toFixed(2)}</span>
          </div>
        </div>

        {/* Depth Path */}
        <div className={`rounded-xl border p-4 ${winner === 'depth' ? 'bg-blue-500/8 border-blue-500/30' : 'bg-zinc-800/30 border-zinc-700'}`}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-bold text-zinc-200">Depth Maximizer</p>
              <p className="text-[10px] text-zinc-600">EB + Breakaway EB (G1–G6)</p>
            </div>
            {winner === 'depth' && (
              <span className="flex items-center gap-1 text-[10px] font-semibold text-blue-400 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full">
                <CheckCircle size={9} /> Selected
              </span>
            )}
          </div>
          <div className="space-y-2">
            {depthComponents.map(c => (
              <div key={`dc-${c.label}`} className={`flex items-center justify-between ${c.disabled ? 'opacity-40' : ''}`}>
                <div>
                  <p className="text-[10px] font-semibold text-zinc-300">{c.label}</p>
                  <p className="text-[9px] text-zinc-600">{c.desc}</p>
                </div>
                <span className={`text-sm font-bold font-mono ${c.value > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>${c.value.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-700 mt-3 pt-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-400">Depth Path Total</span>
            <span className={`text-xl font-bold font-mono ${winner === 'depth' ? 'text-blue-400' : 'text-zinc-400'}`}>${depthPath.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}